import type { ConnectionGroup } from '../ConnectionsByLineMenu.tsx';

import { formatProduct } from './formatProduct.ts';
import type { EnhancedSmartLink } from './smartLinkTypes.ts';

const OTHER_PRODUCT_KEY = 'Others';

export const getSmartLinkProductGroupLabel = (link: EnhancedSmartLink): string =>
	// Atlas links collapse under a single "Goals" group; provider casing (e.g.
	// GitHub, OneDrive) is preserved; everything else is Title-Cased. Product-less
	// links fall into the catch-all "Others" bucket.
	formatProduct(link.product) ?? OTHER_PRODUCT_KEY;

const getProductKey = (link: EnhancedSmartLink): string => {
	if (link.access === 'unauthorized') {
		const authKey = link.authProviders[0]?.key;
		if (authKey) {
			return authKey;
		}
	}
	return link.product || OTHER_PRODUCT_KEY;
};

const isUnconnected = (link: EnhancedSmartLink): boolean => link.access === 'unauthorized';

interface LinkBucket {
	product: string;
	productKey: string;
	// Links are partitioned as they arrive so we never iterate a group's items
	// a second time. Unconnected links lead, connected follow; each keeps
	// document order, which is the within-group ordering we render.
	unconnectedLinks: EnhancedSmartLink[];
	connectedLinks: EnhancedSmartLink[];
}

// Group ordering tier: unconnected-only groups float to the top, fully connected
// groups sink to the bottom, and mixed groups sit in between. Lower rank = higher up.
const UNCONNECTED_ONLY = 0;
const MIXED = 1;
const CONNECTED_ONLY = 2;

const getGroupRank = (bucket: LinkBucket): 0 | 1 | 2 => {
	if (bucket.unconnectedLinks.length === 0) {
		return CONNECTED_ONLY;
	}
	if (bucket.connectedLinks.length === 0) {
		return UNCONNECTED_ONLY;
	}
	return MIXED;
};

// The catch-all "Others" bucket (product-less links) is always pinned to the very
// bottom of the menu, below every named-product bucket, regardless of its tier.
const isOtherBucket = (bucket: LinkBucket): boolean => bucket.product === OTHER_PRODUCT_KEY;

export function groupSmartLinksByProduct(links: EnhancedSmartLink[]): ConnectionGroup[] {
	const buckets = new Map<string, LinkBucket>();

	for (const link of links) {
		const label = getSmartLinkProductGroupLabel(link);
		let bucket = buckets.get(label);
		if (!bucket) {
			bucket = {
				product: label,
				productKey: getProductKey(link),
				unconnectedLinks: [],
				connectedLinks: [],
			};
			buckets.set(label, bucket);
		}
		(isUnconnected(link) ? bucket.unconnectedLinks : bucket.connectedLinks).push(link);
	}

	const allBuckets = Array.from(buckets.values());
	const productBuckets = allBuckets.filter((bucket) => !isOtherBucket(bucket));
	const otherBucket = allBuckets.find(isOtherBucket);

	// Stable sort keeps groups of equal rank in first-appearance order. "Others"
	// is held out of the sort and appended last so it always sits at the bottom.
	const orderedBuckets = productBuckets.sort((a, b) => getGroupRank(a) - getGroupRank(b));
	if (otherBucket) {
		orderedBuckets.push(otherBucket);
	}

	return orderedBuckets.map(({ product, productKey, unconnectedLinks, connectedLinks }) => ({
		product,
		productKey,
		items: [...unconnectedLinks, ...connectedLinks],
	}));
}
